import { FileAlreadyExistsError } from "@/lib/data/requests/files/canonical-files";
import { isAxiosError } from "axios";
import type { createFirmFileV1 } from "@/lib/data/requests/firm/firm-file";
import { retrieveFirmFilesV1 } from "@/lib/data/requests/firm/firm-file";
import type { APIResponse, FirmFile } from "@/lib/types";
import {
  ensureFileNameHasExtension,
  getFileNameParts,
} from "@/lib/utils/matterFile";
import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import { automationFileSchema } from "@/lib/data/schemas/automations";
export async function retrieveAttachmentContent(id: string) {
  return automationFileSchema.parse(
    (await authenticatedFastApiAxiosInstance.get(`file-contents/${id}`)).data,
  );
}

/** Attach the file already occupying this destination, or upload a new file. */
export async function attachFirmFile(
  data: Parameters<typeof createFirmFileV1>[0],
  create: (
    data: Parameters<typeof createFirmFileV1>[0],
  ) => Promise<APIResponse<FirmFile>>,
): Promise<APIResponse<FirmFile> & { reused: boolean }> {
  if (data.isFolder || !data.file) throw new Error("Choose a file to attach.");
  const name = ensureFileNameHasExtension(
    data.name.trim(),
    getFileNameParts(data.file.name).fileExtension,
  );
  const findExisting = async () => {
    const response = await retrieveFirmFilesV1({
      exactName: name,
      idLocation: data.idLocation || undefined,
    });
    const existing = response.data.find(
      (item) =>
        !item.isFolder &&
        item.fileContentId &&
        (item.idLocation || null) === (data.idLocation || null) &&
        item.name.toLowerCase() === name.toLowerCase(),
    );
    return existing
      ? { data: existing, meta: response.meta, reused: true }
      : undefined;
  };
  const existing = await findExisting();
  if (existing) return existing;
  try {
    return { ...(await create({ ...data, name })), reused: false };
  } catch (error) {
    // Another upload can create this name between lookup and canonical creation.
    if (
      error instanceof FileAlreadyExistsError ||
      (isAxiosError(error) && error.response?.status === 409)
    ) {
      const concurrent = await findExisting();
      if (concurrent) return concurrent;
    }
    throw error;
  }
}

export async function retrieveAttachmentPreview(
  id: string,
  processingId?: string,
) {
  const { data } = await authenticatedFastApiAxiosInstance.get<{
    fileContentId: string;
    processingId: string | null;
    fileName: string;
    mimeType: string;
    sizeBytes: number;
    url: string;
  }>(`file-contents/${id}/preview`, { params: { processingId } });
  return data;
}
